import { ref, computed, shallowRef } from 'vue'
import { defineStore } from 'pinia'
import type { SuggestedSize, UserSelectedSize } from '@/types/sizes'
import type { SurveyConfig, SurveyStep, ConditionRule } from '../types/survey-config'
import { fetchSurveyConfig, submitSurveyAnswers, fetchSurveyResults } from '../api/survey'
import type { SurveySubmitRequest } from '../api/survey'
import { apiClient } from '@/api/api'
import { queryCache } from '@/queries/core/queryCache'

export type CategoryKey = 'tops' | 'bottoms' | 'dresses' | 'outerwear' | 'lingerie' | 'shoes'

export interface BrandOption {
  value: string
  label: string
}

export interface SystemOption {
  value: string
  label: string
}

// Канонические формы sizing v2 живут в @/types/sizes — переэкспортируем,
// чтобы у стора не расходилась своя копия (импорт для локального
// использования — выше).
export type { SuggestedSize, UserSelectedSize }

/**
 * `GET /survey/results` is camelised by the interceptor: the legacy array is
 * `suggestedSizes` at runtime (`src/types/sizes.ts` already says so); the
 * snake_case copy here never matched and the fallback read `undefined`.
 */
export interface SurveyResults {
  brands?: BrandOption[]
  systems?: SystemOption[]
  tops: { 
    image: string
    title: string
    description: string
    sizes: string[]
    suggestedSizes: string[]
    suggestedSize?: SuggestedSize | null
    userSelectedSize?: UserSelectedSize | string | null
  }
  bottoms: { 
    image: string
    title: string
    description: string
    sizes: string[]
    suggestedSizes: string[]
    suggestedSize?: SuggestedSize | null
    userSelectedSize?: UserSelectedSize | string | null
  }
  dresses: { 
    image: string
    title: string
    description: string
    sizes: string[]
    suggestedSizes: string[]
    suggestedSize?: SuggestedSize | null
    userSelectedSize?: UserSelectedSize | string | null
  }
  outerwear: { 
    image: string
    title: string
    description: string
    sizes: string[]
    suggestedSizes: string[]
    suggestedSize?: SuggestedSize | null
    userSelectedSize?: UserSelectedSize | string | null
  }
  lingerie: { 
    image: string
    title: string
    description: string
    sizes: string[]
    suggestedSizes: string[]
    suggestedSize?: SuggestedSize | null
    userSelectedSize?: UserSelectedSize | string | null
  }
  shoes: { 
    image: string
    title: string
    description: string
    sizes: string[]
    suggestedSizes: string[]
    suggestedSize?: SuggestedSize | null
    userSelectedSize?: UserSelectedSize | string | null
  }
}

const SURVEY_PROGRESS_KEY = 'fair_survey_progress'

interface StoredSurveyProgress {
  configId: string
  surveyData: Record<string, any>
  stepIndex: number
}

/**
 * Half-finished answers live in localStorage so closing the modal mid-way does
 * not throw them away.
 *
 * Unlike the taste survey this never auto-submits on close: a partial size
 * survey would come back with sizes computed from missing measurements, and
 * those are sizes somebody orders clothes in. The answers are restored on the
 * next open instead, and only a deliberate submit reaches the backend.
 */
function saveSurveyProgress(
  configId: string,
  surveyData: Record<string, any>,
  stepIndex: number,
) {
  try {
    localStorage.setItem(
      SURVEY_PROGRESS_KEY,
      JSON.stringify({ configId, surveyData, stepIndex } satisfies StoredSurveyProgress),
    )
  } catch {
    // localStorage may be unavailable (private mode, quota)
  }
}

function loadSurveyProgress(configId: string): StoredSurveyProgress | null {
  try {
    const raw = localStorage.getItem(SURVEY_PROGRESS_KEY)
    if (!raw) return null
    const data = JSON.parse(raw) as StoredSurveyProgress
    if (data.configId !== configId || !data.surveyData) return null
    return data
  } catch {
    return null
  }
}

function clearSurveyProgress() {
  try {
    localStorage.removeItem(SURVEY_PROGRESS_KEY)
  } catch {
    // localStorage may be unavailable
  }
}

export const useSurveyModal = defineStore('surveyModal', () => {
  const surveyModal = ref({
    isShow: false,
    currentStepIndex: 0,
    isLoading: false,
    isLoadingConfig: false,
    config: null as SurveyConfig | null,
    surveyData: {} as Record<string, any>,
    results: null as SurveyResults | null,
    resultsError: null as string | null,
    surveyResponseId: null as number | null
  })

  // Кэш для видимости шагов, чтобы избежать лишних вычислений
  const stepVisibilityCache = new Map<string, boolean>()
  const lastSurveyDataHash = ref<string>('')

  const steps = computed(() => surveyModal.value.config?.steps ?? [])
  const currentStep = computed(() => steps.value[surveyModal.value.currentStepIndex])
  const totalSteps = computed(() => steps.value.length)

  const getCurrentStep = (): SurveyStep | undefined => {
    return currentStep.value
  }

  const getStepById = (stepId: string): SurveyStep | undefined => {
    return steps.value.find(step => step.id === stepId)
  }

  const getStepIndexById = (stepId: string): number => {
    return steps.value.findIndex(step => step.id === stepId)
  }

  const evaluateConditionRule = (rule: ConditionRule, surveyData: Record<string, any>): boolean => {
    const fieldValue = surveyData[rule.field]

    switch (rule.operator) {
      case 'equals':
        return fieldValue === rule.value
      case 'not_equals':
        return fieldValue !== rule.value
      case 'greater_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue > rule.value
      case 'less_than':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue < rule.value
      case 'greater_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue >= rule.value
      case 'less_or_equal':
        return typeof fieldValue === 'number' && typeof rule.value === 'number' && fieldValue <= rule.value
      case 'in':
        return Array.isArray(rule.value) && rule.value.includes(fieldValue)
      case 'not_in':
        return Array.isArray(rule.value) && !rule.value.includes(fieldValue)
      case 'exists':
        return fieldValue !== null && fieldValue !== undefined && fieldValue !== ''
      case 'not_exists':
        return fieldValue === null || fieldValue === undefined || fieldValue === ''
      default:
        return false
    }
  }

  const shouldStepBeVisible = (step: SurveyStep): boolean => {
    if (!step.conditional) return true

    // Создаем хеш для кэширования
    const surveyDataHash = JSON.stringify(surveyModal.value.surveyData)
    const cacheKey = `${step.id}-${surveyDataHash}`

    // Проверяем кэш
    if (lastSurveyDataHash.value === surveyDataHash && stepVisibilityCache.has(cacheKey)) {
      return stepVisibilityCache.get(cacheKey)!
    }

    const condition = step.conditional
    const surveyData = surveyModal.value.surveyData
    let result = true

    // Поддержка старого формата для обратной совместимости
    if (condition.field && condition.value !== undefined) {
      result = surveyData[condition.field] === condition.value
    } else if (condition.rules && condition.rules.length > 0) {
      // Новый формат с правилами
      const logic = condition.logic || 'AND'
      if (logic === 'AND') {
        result = condition.rules.every(rule => evaluateConditionRule(rule, surveyData))
      } else {
        result = condition.rules.some(rule => evaluateConditionRule(rule, surveyData))
      }
    }

    // Обновляем кэш
    if (lastSurveyDataHash.value !== surveyDataHash) {
      stepVisibilityCache.clear()
      lastSurveyDataHash.value = surveyDataHash
    }
    stepVisibilityCache.set(cacheKey, result)

    return result
  }

  const findNextVisibleStepIndex = (startIndex: number, direction: 'forward' | 'backward'): number => {
    if (direction === 'forward') {
      for (let i = startIndex + 1; i < steps.value.length; i++) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    } else {
      for (let i = startIndex - 1; i >= 0; i--) {
        if (shouldStepBeVisible(steps.value[i])) {
          return i
        }
      }
    }
    return -1
  }

  const loadSurveyConfig = async (config: SurveyConfig) => {
    surveyModal.value.config = config
    surveyModal.value.surveyData = {}
    surveyModal.value.results = null
    surveyModal.value.resultsError = null

    // Очищаем кэш при загрузке новой конфигурации
    stepVisibilityCache.clear()
    lastSurveyDataHash.value = ''

    // Restore what the user already answered for this very config.
    const saved = loadSurveyProgress(config.id)
    if (saved) {
      surveyModal.value.surveyData = { ...saved.surveyData }
      lastSurveyDataHash.value = ''
    }

    // Находим первый видимый шаг
    const firstVisibleIndex = findNextVisibleStepIndex(-1, 'forward')
    const fallbackIndex = firstVisibleIndex >= 0 ? firstVisibleIndex : 0

    // A saved step only counts while it still exists and is still reachable.
    const savedStep =
      saved && saved.stepIndex >= 0 && saved.stepIndex < steps.value.length
        ? steps.value[saved.stepIndex]
        : undefined

    surveyModal.value.currentStepIndex =
      savedStep && shouldStepBeVisible(savedStep) ? saved!.stepIndex : fallbackIndex
  }

  const validateCurrentStep = (): boolean => {
    const step = currentStep.value
    if (!step) return false

    if (step.required === false) return true

    if (step.type === 'gender') {
      return surveyModal.value.surveyData.gender !== null && surveyModal.value.surveyData.gender !== undefined
    }

    if (step.type === 'measurement') {
      // Используем step.id как ключ, так как бэкенд возвращает measurement_type (snake_case)
      // и оно может быть undefined. step.id всегда есть и соответствует ключу в answers
      const dataKey = step.id
      const value = surveyModal.value.surveyData[dataKey]
      return value !== null && value !== undefined && value !== ''
    }

    if (step.type === 'shoe_size') {
      const dataKey = step.id
      const value = surveyModal.value.surveyData[dataKey]
      return value !== null && value !== undefined && value !== ''
    }

    if (step.type === 'choice') {
      return surveyModal.value.surveyData[step.id] !== null && surveyModal.value.surveyData[step.id] !== undefined
    }

    return true
  }

  const showSurveyModal = async () => {
    surveyModal.value.isShow = true
    surveyModal.value.isLoadingConfig = true

    try {
      const config = await fetchSurveyConfig({ config_id: 'default-size-survey' })
      await loadSurveyConfig(config)
    } catch (error) {
      console.error('Error loading survey config:', error)
      // Можно показать ошибку пользователю или использовать дефолтную конфигурацию
    } finally {
      surveyModal.value.isLoadingConfig = false
    }
  }

  const showSurveyModalWithResume = async (configId: string) => {
    surveyModal.value.isShow = true
    surveyModal.value.isLoadingConfig = true

    try {
      // Используем params вместо ручного формирования URL для консистентности
      // Важно: query параметры НЕ преобразуются interceptor'ом, поэтому используем snake_case напрямую
      const response = await apiClient.get<{ config: SurveyConfig }>('/survey/config', {
        params: {
          config_id: configId,
          resume: 'true'
        }
      })
      await loadSurveyConfig(response.data.config)
    } catch (error) {
      console.error('Error loading survey config with resume:', error)
      // Fallback to regular config loading
      try {
        const config = await fetchSurveyConfig({ config_id: configId || 'default-size-survey' })
        await loadSurveyConfig(config)
      } catch (fallbackError) {
        console.error('Error loading fallback survey config:', fallbackError)
      }
    } finally {
      surveyModal.value.isLoadingConfig = false
    }
  }

  const hideSurveyModal = () => {
    // Keep the answers so reopening resumes instead of starting over. Nothing
    // is sent to the backend here — see saveSurveyProgress.
    const config = surveyModal.value.config
    const hasAnswers = Object.keys(surveyModal.value.surveyData).length > 0

    if (config && hasAnswers && currentStep.value?.type !== 'welcome') {
      saveSurveyProgress(
        config.id,
        surveyModal.value.surveyData,
        surveyModal.value.currentStepIndex,
      )
    }

    surveyModal.value.isShow = false
  }

  const nextStep = () => {
    if (!validateCurrentStep()) {
      return
    }

    const nextIndex = findNextVisibleStepIndex(surveyModal.value.currentStepIndex, 'forward')
    if (nextIndex >= 0) {
      surveyModal.value.currentStepIndex = nextIndex
    }
  }

  const prevStep = () => {
    const prevIndex = findNextVisibleStepIndex(surveyModal.value.currentStepIndex, 'backward')
    if (prevIndex >= 0) {
      surveyModal.value.currentStepIndex = prevIndex
    }
  }

  const goToStep = (stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex < totalSteps.value) {
      const step = steps.value[stepIndex]
      // Если шаг не видим, находим ближайший видимый
      if (step && !shouldStepBeVisible(step)) {
        const visibleIndex = findNextVisibleStepIndex(stepIndex, 'forward')
        if (visibleIndex >= 0) {
          surveyModal.value.currentStepIndex = visibleIndex
        } else {
          // Если вперед нет видимых, ищем назад
          const visibleIndexBack = findNextVisibleStepIndex(stepIndex, 'backward')
          if (visibleIndexBack >= 0) {
            surveyModal.value.currentStepIndex = visibleIndexBack
          }
        }
      } else {
        surveyModal.value.currentStepIndex = stepIndex
      }
    }
  }

  const goToStepById = (stepId: string) => {
    const index = getStepIndexById(stepId)
    if (index >= 0) {
      goToStep(index)
    }
  }

  const updateSurveyData = (key: string, value: any) => {
    // Используем Object.assign для более эффективного обновления
    surveyModal.value.surveyData = { ...surveyModal.value.surveyData, [key]: value }

    // Инвалидируем кэш видимости, так как данные изменились
    stepVisibilityCache.clear()
    lastSurveyDataHash.value = ''

    // Если текущий шаг стал невидимым из-за изменения данных, переходим к следующему видимому
    const currentStepValue = currentStep.value
    if (currentStepValue && !shouldStepBeVisible(currentStepValue)) {
      const nextVisibleIndex = findNextVisibleStepIndex(surveyModal.value.currentStepIndex, 'forward')
      if (nextVisibleIndex >= 0) {
        surveyModal.value.currentStepIndex = nextVisibleIndex
      } else {
        // Если вперед нет видимых, ищем назад
        const prevVisibleIndex = findNextVisibleStepIndex(surveyModal.value.currentStepIndex, 'backward')
        if (prevVisibleIndex >= 0) {
          surveyModal.value.currentStepIndex = prevVisibleIndex
        }
      }
    }
  }

  const submitSurvey = async () => {
    surveyModal.value.isLoading = true
    surveyModal.value.resultsError = null
    try {
      const config = surveyModal.value.config
      if (!config) {
        throw new Error('Survey config is not loaded')
      }

      // Формируем данные для отправки на бекенд
      const submitData: SurveySubmitRequest = {
        survey_config_id: config.id,
        answers: { ...surveyModal.value.surveyData },
        metadata: {
          completed_at: new Date().toISOString(),
          device_type: typeof window !== 'undefined' && window.innerWidth <= 768 ? 'mobile' : 'desktop',
          language: 'ru'
        }
      }

      // Отправляем данные на бекенд
      const submitResponse = await submitSurveyAnswers(submitData)

      // Данные ответа преобразованы из snake_case в camelCase через caseTransformerInterceptor
      // Сохраняем surveyResponseId
      surveyModal.value.surveyResponseId = submitResponse.surveyResponseId

      // После успешной отправки загружаем результаты используя surveyResponseId
      const results = await fetchSurveyResults({
        surveyResponseId: submitResponse.surveyResponseId,
        brand: 'all',
        system: 'metric'
      })

      // Сохраняем результаты в store
      surveyModal.value.results = results

      // Submitted for real — the draft has served its purpose.
      clearSurveyProgress()

      // Invalidate profile data cache so the profile page shows updated sizes
      queryCache.invalidate(['user-profile', 'profile-data'])
    } catch (error) {
      // Never invent sizes here: fabricated recommendations are acted on by
      // users buying clothes. Surface the failure instead.
      console.error('Error submitting survey:', error)
      surveyModal.value.results = null
      surveyModal.value.resultsError =
        error instanceof Error ? error.message : 'Survey submission failed'
    } finally {
      surveyModal.value.isLoading = false
    }
  }

  return {
    surveyModal,
    steps,
    currentStep,
    totalSteps,
    getCurrentStep,
    getStepById,
    getStepIndexById,
    shouldStepBeVisible,
    findNextVisibleStepIndex,
    loadSurveyConfig,
    validateCurrentStep,
    showSurveyModal,
    showSurveyModalWithResume,
    hideSurveyModal,
    nextStep,
    prevStep,
    goToStep,
    goToStepById,
    updateSurveyData,
    submitSurvey,
    clearSurveyProgress
  }
})


